import React, { createContext, useContext, useState, useEffect } from 'react';

// Create the LocalContext
const LocalContext = createContext();

// Custom hook to use the context
export const useLocalContext = () => {
  const context = useContext(LocalContext);
  if (!context) {
    throw new Error('useLocalContext must be used within a LocalContextProvider');
  }
  return context;
};

// Provider component
export const LocalContextProvider = ({ children }) => {
  // User state
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // UI state
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  
  // Theme state
  const [theme, setTheme] = useState('light');
  
  // Language state
  const [language, setLanguage] = useState('en');
  
  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  
  // Notification state
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'info' // 'success', 'error', 'warning', 'info'
  });
  
  // Form data for login/signup
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    birthday: '',
    gender: '',
    showPassword: false
  });

  // Check for saved user on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('tinderUser');
    if (savedUser) {
      const userData = JSON.parse(savedUser);
      setUser(userData);
      setIsAuthenticated(true);
    }
    
    const savedTheme = localStorage.getItem('tinderTheme');
    if (savedTheme) {
      setTheme(savedTheme);
    }
    
    const savedLanguage = localStorage.getItem('tinderLanguage');
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }
  }, []);

  // Login function
  const login = async (email, password) => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock user data
      const userData = {
        id: Date.now(),
        email,
        name: email.split('@')[0],
        avatar: `https://ui-avatars.com/api/?name=${email.split('@')[0]}&background=fd267a&color=fff`,
        createdAt: new Date().toISOString()
      };
      
      setUser(userData);
      setIsAuthenticated(true);
      localStorage.setItem('tinderUser', JSON.stringify(userData));
      
      showNotification('Welcome back! You have successfully logged in.', 'success');
      closeLoginModal();
      
      return { success: true };
    } catch (error) {
      showNotification('Login failed. Please try again.', 'error');
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Signup function
  const signup = async (userData) => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const newUser = {
        id: Date.now(),
        ...userData,
        avatar: `https://ui-avatars.com/api/?name=${userData.name}&background=fd267a&color=fff`,
        createdAt: new Date().toISOString()
      };
      
      setUser(newUser);
      setIsAuthenticated(true);
      localStorage.setItem('tinderUser', JSON.stringify(newUser));
      
      showNotification('Account created successfully! Welcome to Tinder.', 'success');
      closeSignupModal();
      
      return { success: true };
    } catch (error) {
      showNotification('Signup failed. Please try again.', 'error');
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('tinderUser');
    showNotification('You have been logged out successfully.', 'info');
  };

  // Toggle menu
  const toggleMenu = () => {
    setIsMenuOpen(prev => !prev);
  };

  // Close menu
  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Open login modal
  const openLoginModal = () => {
    setIsLoginModalOpen(true);
    setIsSignupModalOpen(false);
    setActiveTab('login');
  };

  // Close login modal
  const closeLoginModal = () => {
    setIsLoginModalOpen(false);
    resetFormData();
  };

  // Open signup modal
  const openSignupModal = () => {
    setIsSignupModalOpen(true);
    setIsLoginModalOpen(false);
    setActiveTab('signup');
  };

  // Close signup modal
  const closeSignupModal = () => {
    setIsSignupModalOpen(false);
    resetFormData();
  };

  // Reset form data
  const resetFormData = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      name: '',
      phone: '',
      birthday: '',
      gender: '',
      showPassword: false
    });
  };

  // Update form data
  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Show notification
  const showNotification = (message, type = 'info') => {
    setNotification({
      show: true,
      message,
      type
    });
    
    // Auto hide after 4 seconds
    setTimeout(() => {
      hideNotification();
    }, 4000);
  };

  // Hide notification
  const hideNotification = () => {
    setNotification(prev => ({
      ...prev,
      show: false
    }));
  };

  // Toggle theme
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('tinderTheme', newTheme);
  };

  // Change language
  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('tinderLanguage', lang);
  };

  // Update user profile
  const updateUserProfile = (updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('tinderUser', JSON.stringify(updatedUser));
  };

  // Context value
  const value = {
    // User state
    user,
    setUser,
    isAuthenticated,
    login,
    signup,
    logout,
    
    // UI state
    isMenuOpen,
    toggleMenu,
    closeMenu,
    isLoginModalOpen,
    isSignupModalOpen,
    openLoginModal,
    closeLoginModal,
    openSignupModal,
    closeSignupModal,
    activeTab,
    setActiveTab,
    
    // Theme & Language
    theme,
    toggleTheme,
    language,
    changeLanguage,
    
    // Loading
    isLoading,
    setIsLoading,
    
    // Notification
    notification,
    showNotification,
    hideNotification,
    
    // Form data
    formData,
    updateFormData,
    resetFormData,
    
    // Profile
    updateUserProfile
  };

  return (
    <LocalContext.Provider value={value}>
      {children}
    </LocalContext.Provider>
  );
};

export default LocalContext;
